import React, { memo, useEffect, useMemo, useRef, useState } from 'react'

import { Circle, useGoogleMap } from '@react-google-maps/api'
import { RADIUS_CIRCLE } from '../../constants/radius-circle'
import { isModelVisible, lightenDarkenColor } from '../../utils/utils'

const INITIAL_Z_INDEX = 100000
const Z_INDEX_DECREASE = 50

// const debug = "MapMarker"
const debug = false

type Props = {
    data        : any
    grouped?    : boolean
}

function MapMarker(props: Props) {
    const data          = props.data
    const map           = useGoogleMap()
    const markerRef     = useRef<any>(null)
    const subscriber    = useRef<object>({})

    const [zIndex, setZIndex]       = useState(INITIAL_Z_INDEX)
    const [showRadius, setShowRadius] = useState(false)
    const [observersActive, setObserversActive] = useState(false)
    const [showIcon, setShowIcon]   = useState(() => {
        return isModelVisible(data)
    })

    const onClick = () => {
        debug && console.log(debug, "onClick", data)
        setZIndex(z => z - Z_INDEX_DECREASE)

        if (data.triangulationType && !_.isEmpty(data.observerIds)) {
            setObserversActive(active => {
                const next = !active
                window.bus.triggerEvent("toggle-observers-radius", {
                    observerIds : data.observerIds,
                    show        : next
                })
                return next
            })
        } else {
            setShowRadius(r => !r)
        }

        window.bus.triggerEvent("info-panel:show", data)
    }

    useEffect(() => {
        const sub = subscriber.current

        window.bus.subscribe("info-panel:show", sub, (eventData: any) => {
            if (eventData.id === data.id) {
                return
            }
            // this marker is an observer of the triangulated point being shown — its radius is intentionally driven by "toggle-observers-radius", not this cascade
            if ((eventData.observerIds || []).includes(data.id)) {
                return
            }
            if (!window.localSettings.toggleMarkerRadius) {
                setShowRadius(false)
                setObserversActive(false)
            }
        })
        window.bus.subscribe("info-panel:hide", sub, () => {
            if (!window.localSettings.toggleMarkerRadius) {
                setShowRadius(false)
                setObserversActive(false)
            }
        })
        window.bus.subscribe("toggle-observers-radius", sub, (eventData: any) => {
            if (eventData.observerIds.includes(data.id)) {
                setShowRadius(eventData.show)
            }
        })
        window.bus.subscribe("group:select", sub, (items: any[]) => {
            if (items.includes(data)) {
                setShowRadius(true)
            } else if (!window.localSettings.toggleMarkerRadius) {
                setShowRadius(false)
                setObserversActive(false)
            }
            return
        })
        window.bus.subscribe("preferences:filters-changed", sub, () => {
            setShowIcon(isModelVisible(data))
        })

        return () => window.bus.unsubscribe(sub)
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!map || !showIcon || props.grouped) {
            return
        }

        const { icon } = data.markerIcon
        if (!icon?.url) return

        const { scaledSize, anchor, tint } = icon

        let el: HTMLElement
        if (tint) {
            // A mask-image div has no "natural size" to read asynchronously like an <img> does, so fall back to the known PNG dimensions.
            const width  = scaledSize?.width  || 30
            const height = scaledSize?.height || 39

            const div = document.createElement('div')
            div.style.display         = 'block'
            div.style.width           = `${width}px`
            div.style.height          = `${height}px`
            div.style.backgroundColor = tint
            div.style.maskImage       = `url(${icon.url})`
            div.style.maskRepeat      = 'no-repeat'
            div.style.maskSize        = 'contain'
            ;(div.style as any).webkitMaskImage  = `url(${icon.url})`
            ;(div.style as any).webkitMaskRepeat = 'no-repeat'
            ;(div.style as any).webkitMaskSize   = 'contain'
            if (anchor) div.style.transform = `translate(${width / 2 - anchor.x}px, ${height - anchor.y}px)`

            el = div
        } else {
            const img = document.createElement('img')
            img.src = icon.url
            img.style.display = 'block'
            if (scaledSize) {
                img.style.width  = `${scaledSize.width}px`
                img.style.height = `${scaledSize.height}px`
            }

            const applyAnchor = () => {
                if (!anchor) return
                const w = parseInt(img.style.width)  || img.naturalWidth  || 24
                const h = parseInt(img.style.height) || img.naturalHeight || 24
                img.style.transform = `translate(${w / 2 - anchor.x}px, ${h - anchor.y}px)`
            }
            if (img.complete) applyAnchor()
            else img.onload = applyAnchor

            el = img
        }

        const marker = new (window as any).google.maps.marker.AdvancedMarkerElement({
            position : data.position,
            map,
            content  : el,
            zIndex,
            title    : data.name,
        })
        marker.addListener('gmp-click', onClick)
        markerRef.current = marker

        return () => {
            markerRef.current.map = null
            markerRef.current = null
        }
    }, [map, showIcon, props.grouped, data]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (markerRef.current) markerRef.current.zIndex = zIndex
    }, [zIndex])

    const circleOptions = useMemo(() => {
        const options = _.assign({}, RADIUS_CIRCLE, {
            radius  : data.radius
        })
        if (data.customColor) {
            options.strokeColor = data.customColor
            options.fillColor   = lightenDarkenColor(data.customColor, 20)
        }
        return options
    }, [data])

    debug && console.log(debug, "render", showIcon, showRadius, data)

    return (
        <>
            {showRadius &&
                <Circle
                    center  = {data.position}
                    options = {circleOptions}
                />}
        </>
    )
}

// Map re-renders on zoom/filters/data; skip markers whose data and grouped flag didn't change
export default memo(MapMarker)
